import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/styles.css';
import { toast } from 'react-toastify';
import { getEvaPorId, patchEva, getBat, getTipoExamen, getCompanies, getBatById, getPatientById, getNombresMedico } from '../services/api';
import fieldConfig from '../config/fieldConfig';

const EditarEvaluacion = () => {
  const { evaluacionId } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fecha: '',
    cargo: '',
    nombre_medico: '',
    id_empresa: '',
    id_bats: [],
    tipos_examen: []
  });

  const [nombresMedicoDisponibles, setNombresMedicoDisponibles] = useState([]);
  const [listaBaterias, setListaBaterias] = useState([]);
  const [listaExamenes, setListaExamenes] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [paciente, setPaciente] = useState(null);
  const [campoSangreSelect,setCampoSangreSelect]=useState([]);
  const [bateriasExamenesMap, SetBateriasExamenesMap] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // obtener evaluación por id
        const { data } = await getEvaPorId(evaluacionId);
        setFormData({
          fecha: data.fecha,
          cargo: data.cargo,
          nombre_medico: data.nombre_medico || '',
          id_empresa: data.id_empresa || '',
          id_bats: data.id_bats || [],
          tipos_examen: data.tipos_examen.map(te => te.codigo)
        });

        const relacionSangre = data.tipos_examen.find(te=>te.codigo ==='sangre');
        setCampoSangreSelect(relacionSangre?.parametros_sol || []);

        const pacienteResponse = await getPatientById(data.id_paciente);
        setPaciente(pacienteResponse.data);

        const bateriasResponse = await getBat();
        const batsData = bateriasResponse.data
        setListaBaterias(batsData);

        const examenesResponse = await getTipoExamen();
        setListaExamenes(examenesResponse.data);

        const empresasResponse = await getCompanies();
        setEmpresas(empresasResponse.data);

        try {
          const medicosResponse = await getNombresMedico();
          setNombresMedicoDisponibles(medicosResponse.data?.nombres_medico || []);
        } catch (error) {
          console.error('Error al obtener nombres de médico:', error);
          toast.error('No se pudieron cargar los nombres de médicos disponibles.');
        }

        const mapDetalles = {};
        const bateriasConError = [];
        await Promise.all(
          batsData.map(async(b)=>{
            try{
              const res = await getBatById(b.id_bat);
              mapDetalles[b.id_bat]= res.data.tipo_examenes.map(te=>te.codigo);
            }catch(err){
              console.error(`Error al traer detalles de bateria ${b.id_bat}`,err);
              mapDetalles[b.id_bat]=[];
              bateriasConError.push(b.nombre);
            }
          })
        );
        if (bateriasConError.length > 0) {
          toast.error(`No se pudieron cargar los exámenes de: ${bateriasConError.join(', ')}`);
        }
        SetBateriasExamenesMap(mapDetalles);

      } catch (error) {
        console.error('Error al obtener la evaluación:', error);
        setError('No se pudo cargar la evaluación.');
      }
    };

    fetchData();
  }, [evaluacionId]);

  const nombreCompleto = [
    paciente?.nombre,
    paciente?.apellido_p,
    paciente?.apellido_m
  ].filter(Boolean).join(' ');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBateriaCheckboxChange = (idBat) => {
    const seleccionada = formData.id_bats.includes(idBat);
    const nuevasBatsSelected = seleccionada ? formData.id_bats.filter(b=>b !== idBat)
    : [...formData.id_bats, idBat];
    const examenesBat = bateriasExamenesMap[idBat] || [];
    let nuevosExamenes = [...formData.tipos_examen];

    if(!seleccionada){
      nuevosExamenes = [...new Set([...nuevosExamenes, ...examenesBat])];
    }else{
      const ExamenesOtrasBaterias = nuevasBatsSelected.flatMap(
        id => bateriasExamenesMap[id] || []
      );
      nuevosExamenes = nuevosExamenes.filter(codigo => 
        !examenesBat.includes(codigo) || ExamenesOtrasBaterias.includes(codigo)
      );
    }
    setFormData(prev => ({
      ...prev,
      id_bats:nuevasBatsSelected,
      tipos_examen:nuevosExamenes
    }));
  };

  const handleExamenChange = (codigo) => {
    setFormData(prev => ({
      ...prev,
      tipos_examen: prev.tipos_examen.includes(codigo)
        ? prev.tipos_examen.filter(id => id !== codigo)
        : [...prev.tipos_examen, codigo]
    }));
  };
  const handleSubcampoSangreChange = (fieldName) => {
    setCampoSangreSelect((prev) =>
      prev.includes(fieldName) ? prev.filter((name) => name !== fieldName) : [...prev,fieldName]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        fecha: formData.fecha,
        cargo: formData.cargo,
        nombre_medico: formData.nombre_medico || null,
        tipos_examen: formData.tipos_examen.map(codigo => codigo.toString()),
        id_bats: formData.id_bats,
        campos_sangre: formData.tipos_examen.includes('sangre') ? campoSangreSelect : []
      };

      if (formData.id_empresa) {
        payload.id_empresa = formData.id_empresa;
      }

      await patchEva(evaluacionId, payload);
      toast.success('Evaluación actualizada exitosamente.');
      navigate('/evaluaciones');
    } catch (error) {
      console.error('Error al actualizar la evaluación:', error);
      toast.error('Error al actualizar la evaluación.');
    }
  };

  if (error) return <p className="required-text">{error}</p>;

  return (
    <div className="content-wrapper">
      <h1 className="section-title">Editar Evaluación</h1>
      <div className="container-admin">
        <div className="container">
          <h2>Información de la Evaluación</h2>

          {paciente && (
            <div className="form-group">
              <label className='form-label'>Paciente: </label>
              {nombreCompleto} | RUT:{paciente.rut}
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Empresa</label>
              <select
                className="form-control"
                value={formData.id_empresa}
                onChange={(e) => handleChange('id_empresa', e.target.value)}
              >
                <option value="">Seleccione una empresa</option>
                {empresas.map((empresa) => (
                  <option key={empresa.id_usuario} value={empresa.id_usuario}>
                    {empresa.nombre_legal}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Fecha</label>
              <input
                type="date"
                className="form-control"
                value={formData.fecha}
                onChange={(e) => handleChange('fecha', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Cargo</label>
              <input
                type="text"
                className="form-control"
                value={formData.cargo}
                onChange={(e) => handleChange('cargo', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Nombre del Médico</label>
              <select
                className="form-control"
                value={formData.nombre_medico}
                onChange={(e) => handleChange('nombre_medico', e.target.value)}
              >
                <option value="">Sin médico asignado</option>
                {nombresMedicoDisponibles.map((nombre) => (
                  <option key={nombre} value={nombre}>{nombre}</option>
                ))}
              </select>
              {nombresMedicoDisponibles.length === 0 && (
                <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                  Todavía no hay médicos con firma cargada. Sube una firma desde el panel de administrador.
                </p>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Baterías</label>
              <div className='checkbox-group'>
                {listaBaterias.map((bateria) => (
                  <div className='checkbox-item' key={bateria.id_bat} >
                    <input
                      type="checkbox"
                      id={`bateria-${bateria.id_bat}`}
                      value={bateria.id_bat}
                      checked={formData.id_bats.includes(bateria.id_bat)}
                      onChange={() => handleBateriaCheckboxChange(bateria.id_bat)}
                    />
                    <label htmlFor={`bateria-${bateria.id_bat}`}>{bateria.nombre} </label>
                  </div>
                ))}

              </div>
            </div>

            <div className="form-group">
              <h2 className="form-label">Exámenes</h2>
              <div className="checkbox-group">
                {listaExamenes.map((examen) => {
                  const esSangre = examen.codigo === 'sangre';
                  const estaSeleccionado = formData.tipos_examen.includes(examen.codigo);
                  return(
                    <div className='examen-container-item' key={examen.codigo} >
                      <div className="checkbox-item">
                        <input
                          type="checkbox"
                          id={`examen-${examen.codigo}`}
                          value={examen.codigo}
                          checked={estaSeleccionado}
                          onChange={() => handleExamenChange(examen.codigo)}
                        />
                        <label htmlFor={`examen-${examen.codigo}`}>{examen.nombre}</label>
                      </div>

                      {esSangre && estaSeleccionado &&(
                        <div className='sub-campos-sangre'>
                          <p className='sub-campos-titulo'> Parametros del examen de sangre a evaluar:</p>
                          <div className='checkbox-grid-sangre'>
                            {fieldConfig.sangre.map((item) => (
                              <div className='checkbox-item' key={item.name}>
                                <input
                                  type='checkbox'
                                  id={`subcampo-${item.name}`}
                                  checked={campoSangreSelect.includes(item.name)}
                                  onChange={() => handleSubcampoSangreChange(item.name)}
                                />
                                <label htmlFor={`subcampo-${item.name}`}>{item.label}</label>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="form-group btn-group">
              <button type="submit" className="btn btn-submit">
                Guardar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditarEvaluacion;